/* eslint-disable camelcase */
import type { RJSFSchema } from '@rjsf/utils';
import { memo, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { FormValue } from '../../form-value';
import type { DetailedFile } from '../widgets/detailed-file-uploader-widget';
import { File, PricingValues } from './components';
import { Wrapper } from './schema-form-values.styles';
import type {
  RJSFFile,
  SchemaFormValuesCopy,
  SchemaFormValuesProps,
} from './types';
import { isHiddenByDependency } from './utils';

interface Item {
  const: string;
  title: string;
}

const DEFAULT_COPY: SchemaFormValuesCopy = {
  yes: 'Yes',
  no: 'No',
  downloadFile: 'Download file',
  downloadSuccess: 'File successfully downloaded',
  downloadError: 'Error downloading file',
};

const SchemaFormValues = ({
  schema,
  data,
  valueFormatters,
  copy = DEFAULT_COPY,
}: SchemaFormValuesProps) => {
  const fields = useMemo(() => {
    if (!schema.properties) {
      return;
    }

    return Object.keys(schema.properties).map((key) => {
      const propertySchema = schema.properties?.[key] as RJSFSchema | undefined;
      if (!propertySchema) {
        return null;
      }

      const { type, title, format, oneOf, properties, readOnly } =
        propertySchema;
      const items = propertySchema.items as RJSFSchema | undefined;
      const value = data[key];

      if (valueFormatters?.[key]) {
        const ValueComponent = valueFormatters[key];
        return <FormValue key={key} label={title} value={<ValueComponent />} />;
      }

      if (isHiddenByDependency({ schema, data }, key)) {
        return false;
      }

      if (value === undefined || value === null || value === '') {
        return readOnly ? (
          false
        ) : (
          <FormValue key={key} label={title} value="-" />
        );
      }
      if (type === 'object') {
        if (properties && 'amount' in properties && 'currency' in properties) {
          // Currency amount object
          const objValue = value as
            | { amount?: string | number; currency?: string }
            | undefined;
          return (
            <FormValue
              key={key}
              label={title}
              value={objValue?.amount}
              currency={objValue?.currency}
            />
          );
        }
        if (format === 'pricing') {
          return (
            <PricingValues
              key={key}
              value={value}
              properties={properties as RJSFSchema}
              copy={copy}
            />
          );
        }
        return (
          <SchemaFormValues
            key={key}
            schema={propertySchema}
            data={value as SchemaFormValuesProps['data']}
          />
        );
      }
      if (type === 'array') {
        const arrayValue = Array.isArray(value) ? value : [];
        if (arrayValue.length === 0) {
          return <FormValue key={key} label={title} value={'-'} />;
        }
        if (items?.format === 'data-url') {
          if (items?.type === 'object') {
            return (
              <FormValue
                key={key}
                label={title}
                value={
                  <FileList>
                    {arrayValue.map((item, index: number) => {
                      const detailedFile = item as DetailedFile;
                      return (
                        <File
                          key={`${key}-file-${index}`}
                          value={detailedFile.document}
                          copy={copy}
                          name={detailedFile.name}
                          description={detailedFile.description}
                          isPolicyDocument={detailedFile.isPolicyDocument}
                        />
                      );
                    })}
                  </FileList>
                }
              />
            );
          }
          return (
            <FormValue
              key={key}
              label={title}
              value={arrayValue.map((item, index: number) => (
                <File
                  key={`${key}-file-${index}`}
                  value={item as string | RJSFFile}
                  copy={copy}
                />
              ))}
            />
          );
        }
        if (
          items?.anyOf &&
          Array.isArray(items.anyOf) &&
          items.anyOf.length > 0
        ) {
          return (
            <FormValue
              key={key}
              label={title}
              value={
                <List>
                  {arrayValue.map((item) => {
                    const str = String(item);
                    const anyOfItem = (items.anyOf as Item[] | undefined)?.find(
                      (anyItem) => str === anyItem.const
                    );
                    return <li key={str}>{anyOfItem?.title ?? str}</li>;
                  })}
                </List>
              }
            />
          );
        }
        return (
          <FormValue
            key={key}
            label={title}
            value={arrayValue.map((item) => {
              if (
                typeof item === 'object' &&
                item !== null &&
                'id' in item &&
                'title' in item
              ) {
                const objItem = item as { id: string; title: string };
                return <div key={objItem.id}>{objItem.title}</div>;
              }
              const str = String(item);
              return <div key={str}>{str}</div>;
            })}
          />
        );
      }
      if (type === 'boolean') {
        return (
          <FormValue
            key={key}
            label={title}
            value={value ? copy.yes : copy.no}
          />
        );
      }
      if (format === 'date') {
        return <FormValue key={key} label={title} date={value as string} />;
      }
      if (format === 'data-url') {
        return (
          <FormValue
            key={key}
            label={title}
            value={<File value={value as string | RJSFFile} copy={copy} />}
          />
        );
      }
      if (format === 'rich-text') {
        return <FormValue key={key} label={title} html={value as string} />;
      }
      if (oneOf && Array.isArray(oneOf)) {
        // Show select option title as the value
        const selectOption = (oneOf as RJSFSchema[]).find(
          (item) => item.const === value
        );
        return (
          <FormValue
            key={key}
            label={title}
            value={selectOption?.title ?? String(value)}
          />
        );
      }
      return (
        <FormValue key={key} label={title} value={value as string | number} />
      );
    });
  }, [schema, data, valueFormatters, copy]);

  return <Wrapper>{fields}</Wrapper>;
};

export default memo(SchemaFormValues);

const List = styled.ul`
  ${({ theme }) => css`
    margin-top: ${theme.spacing(0.5)};
    padding-left: 0;
    list-style: none;

    li:before {
      content: '•';
      font-size: 14px;
      padding: ${theme.spacing(0, 1)};
    }
  `}
`;

const FileList = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
    margin-top: ${theme.spacing(1.5)};
  `}
`;
